import React, { useState } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { Medication } from '../../types/medication'

interface StockStatus {
  status: 'excellent' | 'good' | 'warning' | 'critical' | 'empty'
  color: string
  bgColor: string
  textColor: string
}

const StocksSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { medications, loading } = useMedications()

  const getStockStatus = (medication: Medication): StockStatus => {
    const currentStock = medication.currentStock || medication.stock || 0
    const maxStock = medication.maxStock || 100
    const stockPercentage = (currentStock / maxStock) * 100
    const lowStockThreshold = medication.lowStockThreshold || 10

    if (currentStock === 0) {
      return {
        status: 'empty',
        color: 'text-gray-500',
        bgColor: 'bg-gray-400',
        textColor: 'text-gray-600'
      }
    } else if (currentStock <= lowStockThreshold) {
      return {
        status: 'critical',
        color: 'text-red-600',
        bgColor: 'bg-red-500',
        textColor: 'text-red-600'
      }
    } else if (stockPercentage <= 25) {
      return {
        status: 'warning',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-500',
        textColor: 'text-yellow-600'
      }
    } else if (stockPercentage <= 60) {
      return {
        status: 'good',
        color: 'text-green-600',
        bgColor: 'bg-green-500',
        textColor: 'text-green-600'
      }
    } else {
      return {
        status: 'excellent',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-500',
        textColor: 'text-emerald-600'
      }
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        )
      case 'warning':
      case 'critical':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        )
      case 'empty':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        )
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'excellent': return 'Excelente'
      case 'good': return 'Bien'
      case 'warning': return 'Bajo'
      case 'critical': return 'Crítico'
      case 'empty': return 'Agotado'
      default: return 'Desconocido'
    }
  }

  // Filter medications that have stock data
  const medicationsWithStock = medications.filter(med => {
    const currentStock = med.currentStock || med.stock || 0
    const maxStock = med.maxStock || 0
    return maxStock > 0 || currentStock > 0
  })

  // Sort by stock percentage (lowest first)
  const sortedMedications = medicationsWithStock.sort((a, b) => {
    const aCurrentStock = a.currentStock || a.stock || 0
    const aMaxStock = a.maxStock || 100
    const bCurrentStock = b.currentStock || b.stock || 0
    const bMaxStock = b.maxStock || 100
    
    const aPercentage = (aCurrentStock / aMaxStock) * 100
    const bPercentage = (bCurrentStock / bMaxStock) * 100
    
    return aPercentage - bPercentage
  })

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left focus:outline-none"
      >
        <div className="flex items-center">
          <svg className="w-6 h-6 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stocks</h3>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      
      {isExpanded && (
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Cargando stocks...</p>
              </div>
            </div>
          ) : sortedMedications.length === 0 ? (
            <div className="text-center py-6">
              <svg className="mx-auto h-10 w-10 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
              <p className="text-gray-500 dark:text-gray-400 text-sm">No hay medicamentos con stock registrado</p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '50%'}}>
                      Medicamento
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '25%'}}>
                      Stock
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '25%'}}>
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {sortedMedications.map((medication) => {
                    const currentStock = medication.currentStock || medication.stock || 0
                    const maxStock = medication.maxStock || 100
                    const stockPercentage = (currentStock / maxStock) * 100
                    const stockStatus = getStockStatus(medication)
                    
                    return (
                      <tr key={medication.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-2 py-3 text-left" style={{width: '50%'}}>
                          <div className="flex items-center min-w-0">
                            <div className="w-8 h-8 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                              {medication.pillSvg ? (
                                <div 
                                  dangerouslySetInnerHTML={{
                                    __html: medication.pillSvg
                                      .replace('width="60"', 'width="24"')
                                      .replace('height="60"', 'height="24"')
                                  }}
                                />
                              ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                {medication.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {medication.dosage}mg
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-center" style={{width: '25%'}}>
                          <div className="flex flex-col items-center">
                            <span className={`text-sm font-semibold ${stockStatus.textColor}`}>
                              {currentStock}/{maxStock}
                            </span>
                            <div className="w-full max-w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 mt-1">
                              <div 
                                className={`h-1.5 rounded-full ${stockStatus.bgColor} transition-all duration-300`}
                                style={{width: `${Math.min(stockPercentage, 100)}%`}}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {Math.round(stockPercentage)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-center" style={{width: '25%'}}>
                          <div className="flex justify-center">
                            <span 
                              className={`${stockStatus.color} transition-colors`}
                              title={getStatusText(stockStatus.status)}
                            >
                              {getStatusIcon(stockStatus.status)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StocksSection 